// PawnerForm.jsx — wide, no scroll, uses PawnerForm.css only
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import usePersonScreening from "../hooks/usePersonScreening";
import ScreeningWarning from "./ScreeningWarning";
import "./PawnerForm.css";
import useFormDraft from "../hooks/useFormDraft";

const TXN_SHARE_KEY = "grantFormTransactionDraft";
const PAWNER_DRAFT_KEY = "pawnerFormDraft";

export default function PawnerForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const goldDetails = JSON.parse(localStorage.getItem("pawnGoldDetails") || "{}");

  const isPositiveNumber = (s) => {
    const n = Number(String(s ?? "").toString().replace(/,/g, "").trim());
    return Number.isFinite(n) && n > 0;
  };

  const validateGrantTx = (tx) => {
    if (!tx) return { ok: false, reason: "Missing transaction data" };
    const errs = [];

    if (!String(tx.items || "").trim()) errs.push("Items");
    if (!(tx.noOfItem > 0)) errs.push("No. of Items");
    if (!isPositiveNumber(tx.principal ?? tx.amount)) errs.push("Amount");
    if (!isPositiveNumber(tx.weight)) errs.push("Weight");
    if (!isPositiveNumber(tx.valuation ?? tx.valuationAmount)) errs.push("Valuation");
    if (!String(tx.remark || "").trim()) errs.push("Remark");
    if (!tx.grantDate) errs.push("Grant Date");
    if (!tx.expiryDate) errs.push("Expiry Date");

    return errs.length ? { ok: false, reason: `Please complete these in Grant Form: ${errs.join(", ")}` } : { ok: true };
  };

  const transactionData =
    (location.state && location.state.transactionData) ||
    (() => {
      try { return JSON.parse(localStorage.getItem(TXN_SHARE_KEY) || "null"); }
      catch { return null; }
    })();

  const [draft, setDraft, clearPawnerDraft] = useFormDraft(PAWNER_DRAFT_KEY, {
    nric: "", name: "", race: "", sex: "", nationality: "",
    sgAddr: "", foreignAddr: "", dob: "", contactNoMobile: "", blacklisted: false
  });

  const [nric, setNric] = useState(draft.nric);
  const [name, setName] = useState(draft.name);
  const [race, setRace] = useState(draft.race);
  const [sex, setSex] = useState(draft.sex);
  const [nationality, setNationality] = useState(draft.nationality);
  const [sgAddr, setSgAddr] = useState(draft.sgAddr);
  const [foreignAddr, setForeignAddr] = useState(draft.foreignAddr);
  const [dob, setDob] = useState(draft.dob);
  const [contactNoMobile, setContactNoMobile] = useState(draft.contactNoMobile);
  const [blacklisted, setBlacklisted] = useState(Boolean(draft.blacklisted));
  const [submitting, setSubmitting] = useState(false);
  const submitInProgress = useRef(false);

  // chooser state
  const [recentChoices, setRecentChoices] = useState([]);
  const [showChooser, setShowChooser] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const screening = usePersonScreening(name, dob, { requireDob: true, context: JSON.stringify([transactionData, nric, name, race, sex, nationality, sgAddr, foreignAddr, dob, contactNoMobile, blacklisted]) });

  // keep draft synced
  useEffect(() => {
    setDraft({ nric, name, race, sex, nationality, sgAddr, foreignAddr, dob, contactNoMobile, blacklisted });
  }, [nric, name, race, sex, nationality, sgAddr, foreignAddr, dob, contactNoMobile, blacklisted, setDraft]);

  const toTitle = (s) => (s || "").toString().toUpperCase();

  const fillFromCustomer = (data) => {
    if (!data) return;
    setNric(data.nirc || "");
    setName(toTitle(data.name));
    setRace(toTitle(data.race));
    setSex(toTitle(data.sex));
    setNationality(toTitle(data.nationality));
    setSgAddr(toTitle(data.sgAddr));
    setForeignAddr(toTitle(data.foreignAddr));
    setContactNoMobile(data.contactNoMobile || "");
    setBlacklisted(Boolean(data.blacklisted));
    const rawDob = data.dob || "";
    setDob(rawDob ? (/^\d{4}-\d{2}-\d{2}$/.test(rawDob) ? rawDob : new Date(rawDob).toISOString().slice(0,10)) : "");
  };

  const loadByNric = async (id) => {
    const ic = (id || "").trim();
    if (!ic) return;
    try {
      const { data } = await axios.get("https://www.lianfoong.com/api/customers/by-nric", { params: { nirc: ic } });
      fillFromCustomer(data);
    } catch {
      try {
        const { data } = await axios.post("https://www.lianfoong.com/api/customers/nric", { nirc: ic });
        fillFromCustomer(data);
      } catch {
        setName(""); setRace(""); setSex(""); setNationality("");
        setSgAddr(""); setForeignAddr(""); setDob(""); setContactNoMobile("");
        setBlacklisted(false);
      }
    }
  };

  const fetchRecentAndDecide = async (minutes = 5) => {
    setLoadingRecent(true);
    setShowChooser(false);
    setAutoFilled(false);
    setRecentChoices([]);
    try {
      const res = await axios.get("https://www.lianfoong.com/api/customers/recent", { params: { minutes }});
      const arr = Array.isArray(res.data) ? res.data : [];
      if (arr.length === 1) {
        fillFromCustomer(arr[0]);
        setAutoFilled(true);
        setTimeout(() => setAutoFilled(false), 3000);
      } else if (arr.length > 1) {
        setRecentChoices(arr);
        setShowChooser(true);
      }
    } catch {} finally { setLoadingRecent(false); }
  };

  // Esc → back
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); navigate(-1); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Screening starts only once the birthdate is available, preserving the Grant trigger.
  const handleNameChange = (e) => setName(toTitle(e.target.value));

  // Enter on NRIC triggers lookup
  const onNricKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); loadByNric(nric); } };

  // cancel → clear local data + back to dashboard
  const handleCancel = () => {
    try {
      localStorage.removeItem(TXN_SHARE_KEY);
      localStorage.removeItem("pawnerFormDraft");
      localStorage.removeItem("grantFormDraft");
      localStorage.removeItem("adjustGrantDraft");
      localStorage.removeItem("adjustGrantTransactionDraft");
    } catch {}
    try { clearPawnerDraft && clearPawnerDraft(); } catch {}

    setNric(""); setName(""); setRace(""); setSex(""); setNationality("");
    setSgAddr(""); setForeignAddr(""); setDob(""); setContactNoMobile("");
    setBlacklisted(false);

    navigate("/dashboard", { replace: true });
  };

  const handleSubmit = async () => {
    if (submitInProgress.current) return;
    if (!transactionData) { alert("Transaction details missing. Please go back to Grant Form."); return; }
    const check = validateGrantTx(transactionData);
    if (!check.ok) {
      alert(check.reason || "Please complete the Grant Form first.");
      navigate(-1);
      return;
    }

    // --- NEW: Blacklist check ---
    if (blacklisted) {
      const ok = window.confirm("⚠️ This customer is BLACKLISTED.\nAre you sure you want to continue with this transaction?");
      if (!ok) return;
    }

    if (!(await screening.confirmBeforeProceed())) return;

    const enteredHandler = prompt("Please enter your handler code:");
    if (!enteredHandler) { alert("Handler code is required!"); return; }

    submitInProgress.current = true;
    setSubmitting(true);
    let ticketSaved = false;
    let grantRequestStarted = false;
    try {
      await axios.post("https://www.lianfoong.com/api/customers", {
        nirc: nric, name, race, sex, nationality, sgAddr, foreignAddr,
        dob: dob || null, contactNoMobile, blacklisted,
      });

      const goldDetails = JSON.parse(localStorage.getItem("pawnGoldDetails") || "{}");
      const w = (k) => Number(goldDetails?.weights?.[k] ?? 0);
      const p = (k) => Number(goldDetails?.prices?.[k] ?? 0);
      const gold = {
        gold1Qty: w("999"),  gold2Qty: w("24K"),  gold3Qty: w("23K"),
        gold4Qty: w("22K"),  gold5Qty: w("20K"),  gold6Qty: w("18K"),
        gold7Qty: w("14K"),  gold8Qty: w("9K"),   gold9Qty: w("STONE"),
        gold1Price: p("999"), gold2Price: p("24K"), gold3Price: p("23K"),
        gold4Price: p("22K"), gold5Price: p("20K"), gold6Price: p("18K"),
        gold7Price: p("14K"), gold8Price: p("9K"), gold9Price: p("STONE"),
        valAddAmt: Number(goldDetails?.valAdd ?? 0),
      };
      grantRequestStarted = true;
      const response = await axios.post(
        "https://www.lianfoong.com/api/pawn-transactions/grants",
        { transaction: {
          ...transactionData,
          pawnerNirc: nric,
          amount: Number(transactionData?.principal ?? 0),
          valuationAmount: Number(transactionData?.valuation ?? 0),
          item: transactionData?.items,
          weight: transactionData?.weight,
          remark: transactionData?.remark,
          noOfItem: transactionData?.noOfItem,
          grantDate: transactionData?.grantDate,
          expiryDate: transactionData?.expiryDate,
          handler: enteredHandler,
          activeRecord: true,
        }, gold },
        { responseType: "blob" }
      );
      ticketSaved = true;
      
      let receiptNo = response.headers["x-receipt-no"];
      if (!receiptNo) {
        const disp = response.headers["content-disposition"] || response.headers["Content-Disposition"];
        if (disp) {
          const m = disp.match(/receipt_([A-Za-z])(\d{2})(\d{2})(\d{4})/);
          if (m) receiptNo = `${m[1].toUpperCase()}/${m[2]}${m[3]}/${m[4]}`;
        }
      }
      if (!receiptNo) {
        alert("The ticket was saved, but its receipt number was not available to this page. Check Enquiry for the latest ticket before submitting again.");
        return;
      }
      
      let filename = "receipt.pdf";
      const disposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);

      handleCancel(); 
    } catch (error) {
      console.error("Error saving data:", error);
      if (ticketSaved) {
        alert("The ticket was saved, but the receipt could not be downloaded. Check Enquiry and reprint it. Do not submit this grant again.");
      } else if (!grantRequestStarted) {
        alert("Customer details could not be prepared or saved. No ticket was created. Please check the customer information and try again.");
      } else if (!error.response) {
        alert("The connection was interrupted. The ticket may have been saved. Check Enquiry before submitting this grant again.");
      } else {
        const body = error.response.data;
        const message = body instanceof Blob ? await body.text() : typeof body === "string" ? body : "";
        alert(message || "Unable to create ticket. Check Enquiry before trying again or contact support.");
      }
    } finally {
      submitInProgress.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="pf-page">
      <div className="pawner-form pawner-form--wide pf-compact">
        <div className="pf-header">
          <h2 className="pf-title">Pawner Record</h2>
          <div className="pf-meta">
            <button
              className="pf-btn pf-btn--primary"
              type="button"
              onClick={() => fetchRecentAndDecide(5)}
              disabled={loadingRecent}
            >
              {loadingRecent ? "Checking…" : "Retrieve latest"}
            </button>
          </div>
        </div>

        {autoFilled && (
          <div className="pf-alert pf-alert--info">Auto-filled from a customer registered in the last 5 minutes.</div>
        )}
        {showChooser && recentChoices.length > 1 && (
          <div className="recent-chooser">
            <div className="recent-chooser-title">Select customer added in the last 5 minutes</div>
            <ul className="recent-chooser-list">
              {recentChoices.map((c) => (
                <li key={c.nirc} className="recent-chooser-item" onClick={() => { fillFromCustomer(c); setShowChooser(false); }}>
                  <div className="recent-line">
                    <span className="recent-name">{(c.name || "").toUpperCase()}</span>
                    <span className="recent-nric">{c.nirc}</span>
                  </div>
                  <div className="recent-sub">Added: {new Date(c.added).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pf-body-noscroll">
          <div className="pf-grid-12">
            <div className="pf-field col-4">
              <label className="pf-label">NRIC No</label>
              <input
                className="pf-input"
                value={nric}
                onChange={(e) => setNric(e.target.value.toUpperCase())}
                onKeyDown={onNricKeyDown}
                onBlur={() => loadByNric(nric)}
              />
            </div>

            <div className="pf-field col-8">
              <label className="pf-label">Name</label>
              <input className="pf-input" value={name} onChange={handleNameChange} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Race</label>
              <input className="pf-input" value={race} onChange={(e) => setRace(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Sex</label>
              <input className="pf-input" value={sex} onChange={(e) => setSex(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Nationality</label>
              <input className="pf-input" value={nationality} onChange={(e) => setNationality(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-12">
              <label className="pf-label">Address (Singapore)</label>
              <textarea className="pf-input pf-textarea" value={sgAddr} onChange={(e) => setSgAddr(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Birthdate</label>
              <input type="date" className="pf-input" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Contact No (Mobile)</label>
              <input className="pf-input" value={contactNoMobile} onChange={(e) => setContactNoMobile(e.target.value)} />
            </div>

            <div className="pf-field col-12 pf-field--message">
              <ScreeningWarning screening={screening} className="grf-alert grf-alert--danger" />
              {blacklisted && <div className="grf-alert grf-alert--danger" style={{ backgroundColor: '#ffebee', color: '#cc0000', border: '1px solid #ffcdd2' }}>
                ⚠️ WARNING: This customer is marked as BLACKLISTED.
              </div>}
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Blacklisted</label>
              <input
                type="checkbox"
                className="pf-checkbox"
                checked={blacklisted}
                onChange={(e) => setBlacklisted(e.target.checked)}
              />
            </div>
          </div>
        </div>

        <div className="pf-footer-nosticky">
          <button type="button" className="pf-btn pf-btn--ghost" onClick={() => navigate(-1)}>
            Back
          </button>
          <button type="button" className="pf-btn pf-btn--danger" onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="pf-btn pf-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving... Please wait" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
